import React, { useEffect } from 'react';

// Helpers
import { toggleIntercom } from 'Shared/helpers';

// Components
import { Illustrations, WpIllustration } from 'Wpds/components/illustration';
import { WpContainer, WpContainerBody, WpContainerHeader } from 'Wpds/components/container';
import { WpButton } from 'Wpds/components/button';
import { WpIcon } from 'Wpds/components/icon';
import { WpText } from 'Wpds/components/text';
import { WpRadioGroup, WpRadioInput } from 'Wpds/components/radio';

// Styles
import './PrintPageTemplate.scss';

export type PrintOption = {
  label: string;
  descriptionText?: string;
  value: string;
};

type Props = {
  title: string;
  titleMeta?: string | React.ReactNode;
  pages: React.ReactNode[];
  footerMeta?: string | React.ReactNode;
  printOptions?: Array<PrintOption>;
  selectedPrintOption?: string;
  handleUpdatePrintOption?(value: string): void;
  handleClosePrintPreviewWindow?: () => void;
};

const PrintPageTemplate = ({
  title,
  pages,
  footerMeta,
  titleMeta,
  printOptions,
  selectedPrintOption,
  handleUpdatePrintOption,
  handleClosePrintPreviewWindow,
}: Props) => {
  useEffect(() => {
    document.title = title;
  }, []);

  const handlePrintClick = () => window.print();

  useEffect(() => {
    toggleIntercom({ show: false });
    return () => {
      toggleIntercom({ show: true });
    };
  }, []);

  const baseClassName = 'PrintPageTemplate';

  const DocumentTitle = () => (
    <div className={`${baseClassName}__title`}>
      <WpIllustration src={Illustrations.pi_logo_full_red_with_text} className={`${baseClassName}__title__logo`} />
      <h1 className={`${baseClassName}__title__header`}>{title}</h1>
      {titleMeta && <>{titleMeta}</>}
      <div className={`${baseClassName}__title__seperator`} />
    </div>
  );

  const DocumentHeader = ({ className }: { className?: string }) => (
    <div className={`${baseClassName}__footer ${className}`}>
      <div className={`${baseClassName}__footer__meta`}>{footerMeta || title}</div>
      <div className={`${baseClassName}__footer__logo`}>
        <WpIllustration src={Illustrations.pi_logo_full_black_with_text} />
      </div>
    </div>
  );

  const pagesToRender = React.Children.map(pages, (page: React.ReactElement<any>, index: number) => {
    if (index === 0) {
      return (
        <>
          <DocumentTitle />
          {page}
        </>
      );
    }
    return page;
  });

  return (
    <div className={baseClassName}>
      <div className={`${baseClassName}__pages`}>
        {pagesToRender.map((page, index) => (
          <div className={`${baseClassName}__page page-break`} key={`print-page-${index}`}>
            <div className={`${baseClassName}__page__content`}>
              {index > 0 && <DocumentHeader />}
              {page}
            </div>
          </div>
        ))}
      </div>
      <div className={`${baseClassName}__options`}>
        <WpContainer className={`${baseClassName}__options__container`}>
          <WpContainerHeader>
            <WpText tag="h5" bold>
              Print
            </WpText>
          </WpContainerHeader>
          {printOptions && (
            <WpContainerBody>
              <WpRadioGroup
                id="print_option"
                label="Print option"
                labelHidden
                onChange={handleUpdatePrintOption}
                defaultValue={selectedPrintOption}
              >
                {printOptions.map(option => (
                  <WpRadioInput
                    id={option.value}
                    label={option.label}
                    value={option.value}
                    key={`print-option-${option.value}`}
                  />
                ))}
              </WpRadioGroup>
            </WpContainerBody>
          )}
          <WpContainerBody>
            <WpButton block onClick={handlePrintClick} size="lg">
              <WpIcon name="print" />
              Print 360 Feedback
            </WpButton>
          </WpContainerBody>
        </WpContainer>
      </div>
      {handleClosePrintPreviewWindow && (
        <div className={`${baseClassName}__close-btn`}>
          <WpButton size="lg" variant="tertiary" onClick={handleClosePrintPreviewWindow}>
            <WpIcon name="times" />
          </WpButton>
        </div>
      )}
    </div>
  );
};

export default PrintPageTemplate;
